import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from 'rebass/styled-components';
import { FiArrowDown, FiArrowUp } from 'react-icons/fi';
import theme from 'styled-theming';
import styled from 'styled-components';
import {
  Layout,
  Row,
  JSONEditor,
  PolicyEditor,
  Section,
  Heading,
  Stepper,
  Loading,
  Subtitle,
  Link,
  NavigationLink,
  FeatureGate,
  Button,
  ConfirmModal,
  NavigateGuard,
} from '..';

import { Analytics } from '../../sections';
import api from '../../api';
import validation from '../../validation';
import EditorError from '../Editor/EditorError';
import { parseToArray, checkValueType } from '../Editor/util';
import { useAppState, useNotification } from '../../hooks';

const arrowColor = theme('mode', {
  light: (props) => props.theme.colors.dimGray,
  dark: (props) => props.theme.colors.whiteSmoke,
});

const ArrowUp = styled(FiArrowUp)`
  stroke-width: 1.5px;
  stroke: ${arrowColor};
`;

const ArrowDown = styled(FiArrowDown)`
  stroke-width: 1.5px;
  stroke: ${arrowColor};
`;

const LINK_TO_DOCS = 'https://developers.liveperson.com/maven-ai-powered-routing-intent-context-policies.html';

const parseValueToArray = (obj) => {
  const newObject = { ...obj };
  if (Array.isArray(newObject.conditions)) {
    newObject.conditions = newObject.conditions.map((item) => {
      const newItem = { ...item };
      // We only want to parse the value if it's a list
      if (checkValueType(item.value) === 'list') {
        newItem.value = parseToArray(newItem.value);
      }
      if (checkValueType(item.value) === 'channel') {
        newItem.value = item.value.replace(/^%%/, '').replace(/%%$/, '');
      }
      return newItem;
    });
  }
  return newObject;
};

const parseConditionsValueToArrays = (policy) => {
  let updatedPolicy = { ...policy };
  updatedPolicy = parseValueToArray(updatedPolicy);
  if (updatedPolicy.exception && updatedPolicy.exception.conditions) {
    updatedPolicy.exception = parseValueToArray(updatedPolicy.exception);
  }
  return updatedPolicy;
};

function PoliciesCcsV1(props) {
  const {
    policyName, type, subtitle,
  } = props;

  const isMounted = useRef(false);

  const [policies, setPolicies] = useState([]);
  const [temporaryPolicy, setTemporaryPolicy] = useState({});
  const [selectedPolicy, setSelectedPolicy] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFetchingPolicies, setIsFetchingPolicies] = useState(null);
  const [reorderInProgress, setReorderInProgress] = useState(false);
  const [editorVersion, setEditorVersion] = useState(null);
  const [isValid, setIsValid] = useState(true);
  const [confirmModalProps, setConfirmModalProps] = useState({ show: false });
  const {
    features, isLoading: isAppStateLoading, themeMode, accountId: brandId,
  } = useAppState();
  const { addNotification } = useNotification();

  const isEditable = type === 'rules';
  const hasTemporaryPolicy = Object.keys(temporaryPolicy).length > 0;
  const temporaryPolicyIndex = policies.findIndex((item) => item.id === temporaryPolicy.id);
  const isFirstPolicy = temporaryPolicyIndex === 0;
  const isLastPolicy = temporaryPolicyIndex + 1 === policies.length;
  const isEditorVersion2 = editorVersion === 'v2';

  const selectedIndex = (list) => {
    const index = list.findIndex((item) => item.id === (temporaryPolicy && temporaryPolicy.id));
    return Math.max(index, 0);
  };

  const isItemSelected = (item) => {
    if (temporaryPolicy.isTemporary) {
      return temporaryPolicy && temporaryPolicy.id === item.id && item.isTemporary;
    }
    return hasTemporaryPolicy && temporaryPolicy.id === item.id;
  };

  const fetchPolicies = async (isCreatingNew) => {
    if (!isFetchingPolicies) {
      if (isMounted.current) {
        setIsFetchingPolicies(true);
      }
      try {
        const data = await api.mavenPolicies.getPolicies(brandId, type);

        if (isMounted.current) {
          setPolicies(data);
          if (isCreatingNew) {
            setTemporaryPolicy(data[data.length - 1]);
          } else {
            const newPolicy = data.length > 0 ? data[selectedIndex(data)] : {};
            setTemporaryPolicy(newPolicy);
          }
        }
      } catch (error) {
        if (isMounted.current) {
          addNotification(error, 'error');
        }
      }
      if (isMounted.current) {
        setIsFetchingPolicies(false);
      }
    }
  };

  const getPolicyEditorVersion = async () => {
    try {
      const featureResult = await api.mavenFeatures.getFeature('maven.feature.policyEditorV2', brandId);
      if (isMounted.current) {
        setEditorVersion(featureResult ? 'v2' : 'v1');
      }
    } catch (error) {
      if (isMounted.current) {
        setEditorVersion('v1');
        addNotification('Failed to fetch editor version, defaulting to editor V1', 'error');
      }
    }
  };

  const swapArrPositions = (a, b) => policies.map((item, index) => {
    if (index === a) {
      return policies[b];
    }
    if (index === b) {
      return policies[a];
    }
    return item;
  });

  function getSwapId(direction) {
    let reorderedArr = [];
    let returnId = temporaryPolicy.id;
    // determine swapId based on swap direction
    switch (direction) {
      case 'up':
        if (temporaryPolicyIndex >= 1) {
          returnId = policies[temporaryPolicyIndex - 1].id;
        }
        reorderedArr = swapArrPositions(temporaryPolicyIndex, temporaryPolicyIndex - 1);
        break;
      default:
        if (temporaryPolicyIndex <= policies.length - 1) {
          returnId = policies[temporaryPolicyIndex + 1].id;
        }
        reorderedArr = swapArrPositions(temporaryPolicyIndex, temporaryPolicyIndex + 1);
    }
    return {
      returnId,
      changeSwapId: () => {
        if (isMounted.current) {
          setPolicies(reorderedArr);
        }
      },
      resetSwapId: () => {
        if (isMounted.current) {
          setPolicies(policies);
        }
      },
    };
  }

  async function swapError(error) {
    addNotification(error, 'error');
    await fetchPolicies();
  }

  const orderUp = async () => {
    if (reorderInProgress !== 'down') {
      setReorderInProgress('up');
      const { changeSwapId, resetSwapId, returnId } = getSwapId('up');
      changeSwapId();
      try {
        await api.mavenPolicies.swapOrder(brandId, temporaryPolicy.id, returnId);
      } catch (event) {
        await swapError(event);
        resetSwapId();
      }
      if (isMounted.current) {
        setReorderInProgress('');
      }
    }
  };

  const orderDown = async () => {
    if (reorderInProgress !== 'up') {
      setReorderInProgress('down');
      const { changeSwapId, resetSwapId, returnId } = getSwapId('down');
      changeSwapId();
      try {
        await api.mavenPolicies.swapOrder(brandId, temporaryPolicy.id, returnId);
      } catch (event) {
        await swapError(event);
        resetSwapId();
      }
      if (isMounted.current) {
        setReorderInProgress('');
      }
    }
  };

  const confirmDeletePolicy = async () => {
    if (isMounted.current) {
      setConfirmModalProps({
        show: false,
      });
      setIsDeleting(true);
      try {
        await api.mavenPolicies.deletePolicy(brandId, type, temporaryPolicy.id);
        const data = await api.mavenPolicies.getPolicies(brandId, type);
        if (isMounted.current) {
          setPolicies(data);
          setTemporaryPolicy(data.length > 0 ? data[0] : {});
          addNotification('Policy deleted successfully', 'success');
        }
      } catch (error) {
        if (isMounted.current) {
          addNotification(error, 'error');
        }
      }
      if (isMounted.current) {
        setIsEditing(false);
        setIsDeleting(false);
      }
    }
  };

  const deletePolicy = () => {
    setConfirmModalProps({
      show: true,
      detail: `Are you sure you want to delete the policy "${temporaryPolicy.name}"`,
      onConfirm: confirmDeletePolicy,
      onCancel: () => {
        if (isMounted.current) {
          setConfirmModalProps({
            show: false,
          });
        }
      },
    });
  };

  const editPolicy = () => {
    // Strip out unwanted properties for edit mode
    const newPolicy = {
      id: temporaryPolicy.id,
      name: temporaryPolicy.name,
      actions: temporaryPolicy.actions,
      conditions: temporaryPolicy.conditions,
      exception: temporaryPolicy.exception,
      enabled: temporaryPolicy.enabled,
    };

    const newPolicyWithoutEnabled = {
      name: temporaryPolicy.name,
      actions: temporaryPolicy.actions,
      conditions: temporaryPolicy.conditions,
      exception: temporaryPolicy.exception,
    };

    // Add in an empty condition is there is none in the policy
    if (newPolicy.conditions.length === 0) {
      newPolicy.conditions = [{ value: '', type: 'EQUAL', property: '' }];
    }

    // Add in an empty action is there is none in the policy
    if (newPolicy.actions.length === 0) {
      newPolicy.actions = [{ type: 'SEND_MESSAGE', payload: { message: '' } }];
    }

    setTemporaryPolicy(newPolicy);
    // Set a selected policy so that we can preserve atteributes when editing v1 policies as JSON
    setSelectedPolicy(JSON.stringify(newPolicyWithoutEnabled, undefined, 4));
    setIsEditing(true);
  };

  const parseInput = (policy) => {
    try {
      return JSON.parse(policy, undefined, 4);
    } catch (error) {
      addNotification('Invalid JSON, please try again', 'error');
    }
    return false;
  };

  const cleanPolicyErrors = () => {
    if (isEditorVersion2) {
      // Reset the attribute object
      setTemporaryPolicy(validation.removeErrorKeysFromJson(temporaryPolicy));
    }
  };

  const validatePolicy = (obj) => {
    // Remove the isTemporary, placeHolderText, and isUntitled properties
    // so that validation doesn't bork
    // these properties don't need to be validated because they are more for UI management.
    const {
      isUntitled,
      placeHolderText,
      isTemporary,
      accountId,
      id,
      schemaVersion,
      order,
      ...cleanedPolicy
    } = obj;

    // Removing previous error keys
    const cleanedTemporaryPolicy = validation.removeErrorKeysFromJson(cleanedPolicy);

    // Validate
    const {
      passedValidation, validationErrors,
    } = validation.validatePolicy(cleanedPolicy, false, false);

    setIsValid(passedValidation);

    if (passedValidation) {
      // Set object WITHOUT error keys
      setTemporaryPolicy({ isTemporary, id, ...cleanedTemporaryPolicy });
    } else {
      // Add in the error properties
      const cleanedPolicyWithErrors = validation.addPolicyErrorProperty(
        cleanedPolicy, validationErrors,
      );

      // Set object WITH error keys
      setTemporaryPolicy({
        isTemporary, accountId, id, schemaVersion, order, ...cleanedPolicyWithErrors,
      });
    }

    return passedValidation;
  };

  const rebuildPolicy = (policy) => ({
    id: temporaryPolicy.id,
    enabled: temporaryPolicy.enabled,
    name: policy.name || temporaryPolicy.name,
    actions: policy.actions,
    conditions: policy.conditions,
    exception: policy.exception,
  });

  const removeEmptyFallbackSkills = (policy) => {
    const policyWithoutEmptyFallback = { ...policy };
    policyWithoutEmptyFallback.actions.forEach((item, i) => {
      if (item.type === 'TRANSFER_TO_AGENT' && (!item.payload.skillId || item.payload.skillId?.replace(/{{}}/gi, '') === '')) {
        policyWithoutEmptyFallback.actions[i].payload.skillId = '-1';
      }
    });
    return policyWithoutEmptyFallback;
  };

  const tryToSavePolicy = async (policy) => {
    try {
      const { isTemporary, ...otherProps } = policy;
      if (temporaryPolicy.isTemporary) {
        await api.mavenPolicies.createPolicy(brandId, type, { ...otherProps });
      } else {
        await api.mavenPolicies.updatePolicy(
          brandId,
          type,
          temporaryPolicy.id,
          { ...otherProps },
        );
      }
      if (isMounted.current) {
        addNotification('Policy Saved', 'success');
        await fetchPolicies(temporaryPolicy.isTemporary);
      }
      if (isMounted.current) {
        setIsEditing(false);
      }
    } catch (error) {
      if (isMounted.current) {
        // Reset object on error
        addNotification(error, 'error');
        setTemporaryPolicy(policy);
      }
    }
  };

  const savePolicyV1 = async () => {
    const parsedPolicy = parseInput(selectedPolicy);
    const isJSONValid = typeof parsedPolicy === 'object';

    // No need to validate v1 policy
    if (isJSONValid) {
      const rebuiltPolicy = rebuildPolicy(parsedPolicy);
      await tryToSavePolicy(rebuiltPolicy);
    }
  };

  const savePolicyV2 = async () => {
    const parsedConditionsPolicy = parseConditionsValueToArrays(temporaryPolicy);
    const newPolicy = removeEmptyFallbackSkills(parsedConditionsPolicy);
    const passedValidation = validatePolicy(parsedConditionsPolicy);

    if (passedValidation) {
      await tryToSavePolicy(newPolicy);
    } else if (isMounted.current) {
      // If the validation does not pass, set the temporaryPolicy back to the correct version
      setTemporaryPolicy(temporaryPolicy);
    }
  };

  const savePolicy = async () => {
    if (!isSaving) {
      setIsSaving(true);

      if (isEditorVersion2) {
        await savePolicyV2();
      } else {
        await savePolicyV1();
      }

      if (isMounted.current) {
        setIsSaving(false);
      }
    }
  };

  const addPolicy = () => {
    const newPolicy = {
      name: 'New policy',
      enabled: false,
      conditions: [{ value: '', type: 'EQUAL', property: '' }],
      actions: [{ type: 'SEND_MESSAGE', payload: { message: '' } }],
      exception: {},
      isTemporary: true,
      isUntitled: true,
      placeHolderText: 'Enter a new policy name',
    };
    const newPolicyWithoutName = {
      name: 'New policy',
      conditions: [{ value: '', type: 'EQUAL', property: '' }],
      actions: [{ type: 'SEND_MESSAGE', payload: { message: '' } }],
      exception: {},
    };

    setPolicies([...policies, newPolicy]);
    setTemporaryPolicy(newPolicy);
    setSelectedPolicy(JSON.stringify(newPolicyWithoutName, undefined, 4));
    setIsEditing(true);
  };

  const cancelPolicy = async () => {
    // Reset the error values to a clean state
    cleanPolicyErrors();

    if (isMounted.current) {
      setIsEditing(false);
    }

    // Refetch the selected policy
    await fetchPolicies();
  };

  const handlePolicyClick = (policy) => {
    if (!isEditing) {
      setTemporaryPolicy(policy);
    }
  };

  const handleToggleChange = async (policy, value, setIsTogglingSwitch, isSwitchMounted) => {
    const originalPolicies = [...policies];
    if (!isEditing) {
      let newPolicy;
      if (isMounted.current) {
        setPolicies(
          policies.map((item) => {
            if (item.id === policy.id) {
              newPolicy = { ...item, enabled: value };
              setTemporaryPolicy(newPolicy);
              return newPolicy;
            }
            return item;
          }),
        );
      }
      try {
        await api.mavenPolicies.updatePolicy(brandId, type, policy.id, newPolicy);
      } catch (error) {
        if (isMounted.current) {
          setPolicies(originalPolicies);
          addNotification(error, 'error');
        }
      }

      try {
        if (features?.['maven.feature.billingMetrics']) {
          await api.policyBillingMetric.callPolicyMetric(brandId, newPolicy);
        }
      } catch (error) {
        // do nothing
      }
    }
    if (isSwitchMounted) {
      setIsTogglingSwitch(false);
    }
  };

  const handleTextAreaChange = (event) => {
    setSelectedPolicy(event.target.value);
    try {
      const parsedJSON = JSON.parse(event.target.value, undefined, 4);
      setTemporaryPolicy({ ...temporaryPolicy, ...parsedJSON });
    } catch (error) {
      // addNotification(error);
    }
  };

  const editModeButtons = (
    <Flex justifyContent="space-between" flex={1} ml={2}>
      <Flex flex={1} mr={2} alignItems="center">
        <Button variant="secondary" mr={2} onClick={cancelPolicy} data-testid="cancel-button">
          Cancel
        </Button>
        <Button variant="primary" onClick={savePolicy} mr={3} data-testid={`save-button${isSaving ? '-enabled' : ''}`}>
          Save
        </Button>
        <EditorError isValid={isValid} />
      </Flex>
    </Flex>
  );

  const defaultButtons = hasTemporaryPolicy ? (
    <Flex flex={1} ml={2}>
      <Button variant="secondary" onClick={editPolicy} mr={2} data-testid="edit-button">
        Edit
      </Button>
      <Button variant="secondary" onClick={deletePolicy} data-testid="delete-button">
        {isDeleting ? 'Deleting' : 'Delete'}
      </Button>
    </Flex>
  ) : null;

  const orderingButtons = hasTemporaryPolicy && !isEditing ? (
    <Flex alignItems="center">
      <Button
        variant="secondary"
        ml={2}
        onClick={orderUp}
        disabled={!!isFirstPolicy || !!reorderInProgress}
        data-testid={`order-up-button${reorderInProgress === 'up' ? '-loading' : ''}`}
      >
        {reorderInProgress === 'up' ? <Loading size={18} alignItems="center" /> : <ArrowUp size={18} />}
      </Button>
      <Button
        variant="secondary"
        ml={2}
        onClick={orderDown}
        disabled={!!isLastPolicy || !!reorderInProgress}
        data-testid={`order-down-button${reorderInProgress === 'down' ? '-loading' : ''}`}
      >
        {reorderInProgress === 'down' ? <Loading size={18} alignItems="center" /> : <ArrowDown size={18} />}
      </Button>
    </Flex>
  ) : null;

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    async function fetchData() {
      await getPolicyEditorVersion();
      await fetchPolicies();
    }

    if (!isAppStateLoading) {
      fetchData();
    }
  }, [isAppStateLoading]); // eslint-disable-line

  const policyEditor = isEditorVersion2 ? (
    <PolicyEditor
      type="policy"
      isEditing={isEditing}
      themeMode={themeMode}
      temporaryPolicy={temporaryPolicy}
      brandId={brandId}
      addNotification={addNotification}
      validateObject={validatePolicy}
      includeSdeAttribute={!!features?.['maven.feature.sdePage']}
    />
  ) : (
    <JSONEditor
      type="policy"
      isEditing={isEditing}
      selectedObject={temporaryPolicy}
      selectedString={selectedPolicy}
      handleChange={handleTextAreaChange}
    />
  );

  return (
    <Box pb={4}>
      <NavigateGuard
        when={isEditing}
        message="Are you sure you want to leave? Your changes will not be saved"
      />
      <Heading>{policyName}</Heading>
      <Subtitle mb={3}>{subtitle}</Subtitle>
      <Section
        section="routing-getting-started"
        title="Getting started"
        subtitle={(
          <Box>
            Building Intent &amp; Context Policies requires configuring several different features
            in the Conversational AI suite. To complete the remaining steps, please see the
            {' '}
            <Link href={LINK_TO_DOCS} target="_blank" rel="noopener noreferrer">
              documentation
            </Link>
            .
          </Box>
        )}
      >
        <Stepper
          steps={[
            { id: 1, title: 'Manage attributes in Conversation Context Service' },
            { id: 2, title: 'Build routing policies' },
            {
              id: 3,
              title: (
                <Box>
                  Connect to Maven using Maven Bot or
                  {' '}
                  <NavigationLink to="/ask-maven">AskMaven</NavigationLink>
                </Box>
              ),
            },
            { id: 4, title: 'Start using policies' },
          ]}
          activeStep={2}
        />
      </Section>
      <FeatureGate feature="maven.feature.analytics">
        <Analytics
          title="Intent &amp; Context Policy Usage"
          subtitle="The following shows the number of conversations that were handled by Intent &amp; Context Policies."
          addNotification={addNotification}
          brandId={brandId}
          type="routingPolicy"
        />
      </FeatureGate>
      <Section
        title="Manage policies"
        subtitle="Create and manage policies here. You can enable and disable each policy using the toggle switch. Policies are evaluated in the order they are presented here. You can reorder the policies."
      >
        <Layout
          isEditing={isEditing}
          layoutItems={
            isFetchingPolicies !== false
              ? [
                <Flex key="loading" align="center" justifyContent="center" py={3} data-testid="loading-policies">
                  <Loading size={24} />
                </Flex>,
              ]
              : policies.map((policy) => (
                <Row
                  themeMode={themeMode}
                  key={`${policy.name}-${Math.random()}`}
                  data={policy}
                  handleClick={handlePolicyClick}
                  handleChange={handleToggleChange}
                  isActive={isItemSelected(policy)}
                  useIcon
                  useOrdering
                  disableAutoScroll
                  isEditing={isEditing}
                />
              ))
          }
          layoutButtons={
            isEditable && (
              <Flex flex={1} alignItems="flex-end" justifyContent="space-between">
                <Button
                  variant="primary"
                  mr={2}
                  onClick={addPolicy}
                  disabled={isEditing}
                  data-testid="add-policy-button"
                >
                  Add Policy
                </Button>
                {orderingButtons}
              </Flex>
            )
          }
          configButtons={isEditing ? editModeButtons : defaultButtons}
          configPanel={
            hasTemporaryPolicy && (editorVersion !== null) ? (
              policyEditor
            ) : null
          }
        />
      </Section>
      <ConfirmModal {...confirmModalProps} />
    </Box>
  );
}

PoliciesCcsV1.defaultProps = {
  subtitle: undefined,
};

PoliciesCcsV1.propTypes = {
  policyName: PropTypes.string.isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  type: PropTypes.string.isRequired,
};

export default PoliciesCcsV1;
